import { useEffect, useRef } from "react";
import { useSessionStore } from "../store/sessionStore";
import { useGitHubStore } from "../store/githubStore";
import { fetchChannel, mergeSessions, pushChannel } from "./github-sync";

// Background sync for the active channel.
//
// Two effects, each guarded by sync_target + token:
//   - Pull every 10s — GET, compare sha, merge into local if changed
//   - Push on local change — debounced 500ms after updated_at moves
//
// The merge resolves concurrent edits: messages union by id (CRDT
// shape), metadata last-write-wins by updated_at. After a pull-merge,
// `lastSyncedAt` advances so we don't immediately push the same state
// back up (echo loop).

const POLL_MS = 10_000;
const PUSH_DEBOUNCE_MS = 500;

export function useSyncLoop(): void {
  const active = useSessionStore((s) => s.activeChannel());
  const replaceChannel = useSessionStore((s) => s.replaceChannel);
  const setSyncTarget = useSessionStore((s) => s.setSyncTarget);
  const token = useGitHubStore((s) => s.token);

  /** Most recent updated_at that came from (or was confirmed by) a
   *  successful sync round-trip. Local edits that move updated_at past
   *  this value are the ones worth pushing. */
  const lastSyncedAt = useRef<string | null>(null);

  const targetKey = active?.sync_target
    ? `${active.sync_target.owner}/${active.sync_target.repo}/${active.sync_target.path}`
    : null;
  const channelId = active?.id ?? null;

  // ---- Pull loop ----
  useEffect(() => {
    if (!channelId || !targetKey || !token) return;
    let cancelled = false;

    async function pullOnce() {
      const current = useSessionStore.getState().channels.find((c) => c.id === channelId);
      const target = current?.sync_target;
      if (!current || !target || !token) return;
      try {
        const result = await fetchChannel(target, token);
        if (cancelled) return;
        if (result.kind === "absent") {
          // File deleted on GitHub — surface by unpublishing locally.
          console.warn("[sync] channel file missing on GitHub, unpublishing");
          setSyncTarget(current.id, null);
          return;
        }
        if (result.kind === "unchanged") return;
        if (result.kind === "updated" && result.session && result.sha) {
          const latest = useSessionStore.getState().channels.find((c) => c.id === current.id);
          if (!latest) return;
          const merged = mergeSessions(latest, result.session);
          const next = { ...merged, sync_target: { ...target, last_sha: result.sha } };
          lastSyncedAt.current = next.updated_at;
          replaceChannel(current.id, next);
        }
      } catch (e) {
        console.warn("[sync] pull failed:", e);
      }
    }

    pullOnce();
    const id = setInterval(pullOnce, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [channelId, targetKey, token, replaceChannel, setSyncTarget]);

  // ---- Push on local change ----
  useEffect(() => {
    if (!channelId || !targetKey || !token) return;
    if (!active?.updated_at) return;
    if (lastSyncedAt.current === active.updated_at) return;

    const handle = setTimeout(async () => {
      const current = useSessionStore.getState().channels.find((c) => c.id === channelId);
      if (!current?.sync_target || !token) return;
      try {
        const { sha, session } = await pushChannel(current.sync_target, current, token);
        const next = { ...session, sync_target: { ...current.sync_target, last_sha: sha } };
        lastSyncedAt.current = next.updated_at;
        replaceChannel(current.id, next);
      } catch (e) {
        console.warn("[sync] push failed:", e);
      }
    }, PUSH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [active?.updated_at, channelId, targetKey, token, replaceChannel]);
}
